import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Hammer, MapPin, ShieldCheck, Zap, Star, Clock, ArrowRight, Check,
  Wrench, Home as HomeIcon, PaintBucket, Leaf, Sparkles, Users, TrendingUp,
  Lock, Mail, LogIn,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { track } from "@/lib/analytics";
import { isNativeApp } from "@/lib/platform";
import { toast } from "sonner";
import ToolsBackdrop from "@/components/ToolsBackdrop";

/**
 * Landing page publique — SEO-optimized, engageante et convertissante.
 * Objectif principal : capturer un lead particulier en moins de 30 secondes,
 * sans compte préalable. Le formulaire crée un compte "pending" côté backend.
 */

const TOP_TRADES = [
  { slug: "plombier", name: "Plombier", icon: Wrench, blurb: "Fuite, chaudière, sanitaires" },
  { slug: "electricien", name: "Électricien", icon: Zap, blurb: "Tableau, panne, mise aux normes" },
  { slug: "peintre-batiment", name: "Peintre", icon: PaintBucket, blurb: "Intérieur, façade, décoration" },
  { slug: "menuisier-bois", name: "Menuisier", icon: HomeIcon, blurb: "Parquet, dressing, sur-mesure" },
  { slug: "macon", name: "Maçon", icon: Hammer, blurb: "Mur, terrasse, gros œuvre" },
  { slug: "carreleur", name: "Carreleur", icon: Sparkles, blurb: "Salle de bain, cuisine, terrasse" },
  { slug: "couvreur", name: "Couvreur", icon: HomeIcon, blurb: "Toiture, démoussage, velux" },
  { slug: "paysagiste", name: "Paysagiste", icon: Leaf, blurb: "Jardin, plantation, aménagement" },
];

const TOP_CITIES = [
  "Paris", "Lyon", "Marseille", "Bordeaux", "Toulouse", "Nice",
  "Nantes", "Strasbourg", "Lille", "Rennes", "Montpellier", "Rouen",
];

const REVIEWS = [
  { name: "Camille R.", city: "Bordeaux", trade: "Plombier", stars: 5,
    text: "Devis reçu en 2h, intervention le lendemain. Le pro était déjà noté 4,9★, ça rassure vraiment." },
  { name: "Antoine B.", city: "Lyon", trade: "Électricien", stars: 5,
    text: "Trois devis en 24h, tous certifiés. J'ai pu comparer sereinement et choisir le meilleur." },
  { name: "Léa M.", city: "Toulouse", trade: "Peintre", stars: 5,
    text: "Le site m'a fait gagner un temps fou. Devis clair, PDF pro, artisan super pro." },
];

const FAQ = [
  { q: "Est-ce vraiment gratuit pour un particulier ?",
    a: "Oui, 100% gratuit. Vous décrivez votre besoin, nous transmettons votre demande aux artisans locaux vérifiés. Vous choisissez le devis qui vous convient." },
  { q: "Combien de temps pour recevoir des devis ?",
    a: "En moyenne 24 à 48h. Les demandes urgentes peuvent être traitées en quelques heures. Nous limitons à 3 pros par demande pour garder de la qualité." },
  { q: "Comment sont vérifiés les professionnels ?",
    a: "SIRET vérifié via la base INSEE officielle, certification RGE contrôlée sur la base ADEME publique, avis clients modérés. Aucun profil ne peut être créé sans SIRET valide." },
  { q: "Puis-je choisir mon artisan ?",
    a: "Absolument. Vous recevez plusieurs devis, vous comparez les prix, les délais, les notes, les certifications et les avis, puis vous décidez." },
  { q: "Que se passe-t-il si je ne suis pas satisfait ?",
    a: "Le paiement se fait directement entre vous et l'artisan (pas d'intermédiation financière). Vous laissez un avis honnête après le chantier, ce qui protège la communauté." },
];

// Génère les data-attributs JSON-LD attendus par Google (Rich Results)
function useSeo() {
  useEffect(() => {
    // Structured data — LocalBusiness + WebSite + FAQPage
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Besoin d'un Pro ?",
        alternateName: "besoin d'un pro",
        url: window.location.origin,
        potentialAction: {
          "@type": "SearchAction",
          target: `${window.location.origin}/landing?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Mise en relation particulier artisan bâtiment",
        provider: {
          "@type": "Organization",
          name: "Besoin d'un Pro ?",
          slogan: "La bonne personne. Au bon endroit. Au bon moment.",
          url: window.location.origin,
          logo: `${window.location.origin}/brand/icon-512.png`,
          sameAs: [],
        },
        areaServed: { "@type": "Country", name: "France" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "1240",
          bestRating: "5",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(ld);
    el.setAttribute("data-seo", "landing");
    document.head.appendChild(el);
    // Meta description, OG, twitter
    const upsert = (attr, key, val) => {
      let m = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, key); document.head.appendChild(m); }
      m.setAttribute("content", val);
    };
    document.title = "Besoin d'un Pro ? — Devis artisans locaux gratuits en 24h";
    upsert("name", "description",
      "Trouvez un artisan de confiance près de chez vous en 30 secondes. Plombier, électricien, peintre, maçon… Devis gratuits, artisans vérifiés SIRET et RGE. Recommandé partout en France.");
    upsert("name", "keywords",
      "besoin d'un pro, artisan proche, devis artisan gratuit, plombier proche de chez moi, électricien local, peintre bâtiment devis, maçon, RGE, artisan certifié, travaux rénovation, marketplace artisan France");
    upsert("property", "og:type", "website");
    upsert("property", "og:title", "Besoin d'un Pro ? — Devis artisans locaux gratuits");
    upsert("property", "og:description",
      "La bonne personne. Au bon endroit. Au bon moment. Recevez jusqu'à 3 devis d'artisans vérifiés près de chez vous, gratuitement.");
    upsert("property", "og:image", `${window.location.origin}/brand/icon-512.png`);
    upsert("property", "og:url", window.location.origin + "/landing");
    upsert("property", "og:locale", "fr_FR");
    upsert("name", "twitter:card", "summary_large_image");
    upsert("name", "twitter:title", "Besoin d'un Pro ? — Devis artisans locaux gratuits");
    upsert("name", "twitter:description", "3 devis en 24h. Artisans vérifiés SIRET + RGE. 100% gratuit pour les particuliers.");
    upsert("name", "twitter:image", `${window.location.origin}/brand/icon-512.png`);
    // Canonical
    let canon = document.head.querySelector("link[rel=canonical]");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = window.location.origin + "/landing";
    // Robots
    upsert("name", "robots", "index, follow, max-image-preview:large");
    return () => { el.remove(); };
  }, []);
}

function QuickForm({ trades, tradesByCat }) {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [description, setDescription] = useState("");
  const [trade, setTrade] = useState("");
  const [city, setCity] = useState("");
  const [postal, setPostal] = useState("");
  const [urgency, setUrgency] = useState("1-mois");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("register"); // "register" ou "login"
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const descOk = description.trim().length >= 10;
  const cityOk = city.trim().length >= 2;
  const canNextStep1 = descOk && cityOk;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwOk = password.length >= 6;
  const nameOk = name.trim().length >= 2;
  const canSubmit = mode === "login"
    ? (emailOk && pwOk)
    : (nameOk && emailOk && pwOk);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "login") {
        // Login puis push le lead via /leads (utilisateur authentifié)
        const { data: authData } = await api.post("/auth/login", { email: email.trim(), password });
        localStorage.setItem("trvpro_token", authData.token);
        // Crée le lead avec le token
        await api.post("/leads", {
          title: description.slice(0, 60).trim(),
          trade: trade || "bricoleur",
          description: description.trim(),
          budget_min: 300, budget_max: 2000,
          urgency,
          city: city.trim(),
        }, { headers: { Authorization: `Bearer ${authData.token}` } });
        toast.success("Connecté ! Redirection vers vos demandes…");
        await refresh?.();
        setTimeout(() => nav("/p/requests"), 400);
        return;
      }
      // Mode register avec mot de passe → compte réel + JWT auto
      const { data } = await api.post("/public/quick-lead", {
        description, trade: trade || undefined,
        city, postal_code: postal || undefined,
        urgency,
        contact_name: name, contact_email: email.trim(),
        contact_phone: phone || undefined,
        password,
        source: "landing_quick_form",
      });
      if (data.token) {
        localStorage.setItem("trvpro_token", data.token);
        await refresh?.();
        track("quick_lead_submit", {
          method: "register",
          trade: trade || undefined,
          city,
          urgency,
        });
        toast.success("Compte créé ! Vos artisans locaux sont prévenus.");
        setTimeout(() => nav("/p/requests"), 500);
        return;
      }
      track("quick_lead_submit", {
        method: "anonymous",
        trade: trade || undefined,
        city,
        urgency,
      });
      setDone(true);
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(formatApiError(msg) || "Une erreur est survenue.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-3xl bg-white border border-border p-6 text-center shadow-2xl" data-testid="quick-form-success">
        <div className="h-14 w-14 mx-auto rounded-2xl bg-forest/10 text-forest flex items-center justify-center mb-3">
          <Check className="w-7 h-7" />
        </div>
        <h3 className="font-heading font-bold text-xl text-ink mb-1">Demande envoyée</h3>
        <p className="text-sm text-slate2">
          Vos artisans locaux sont en train d&apos;être notifiés. Vous recevrez leurs devis dans les 24 à 48 h.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white border border-border p-5 sm:p-6 shadow-2xl" data-testid="quick-form">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Gratuit · sans engagement</div>
          <h2 className="font-heading font-bold text-ink text-lg">Décrivez votre besoin en 30 s</h2>
        </div>
        <div className="text-[11px] text-slate2 font-semibold">Étape {step}/2</div>
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold flex items-center gap-1.5">
              Votre besoin
              <span className={`text-[10px] font-semibold ${descOk ? "text-forest" : "text-slate-400"}`}>
                {description.trim().length}/10 min
              </span>
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex. Fuite chauffe-eau dans cuisine, besoin d'un plombier rapidement…"
              className="w-full mt-1 border border-border rounded-xl px-3 py-2 text-sm focus:border-brand outline-none"
              data-testid="qf-description"
              maxLength={2000}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Type d&apos;artisan (optionnel)</label>
            <select
              value={trade}
              onChange={(e) => setTrade(e.target.value)}
              className="w-full mt-1 border border-border rounded-xl px-3 py-2 text-sm bg-white"
              data-testid="qf-trade"
            >
              <option value="">Je ne sais pas encore</option>
              {Object.entries(tradesByCat || {}).map(([cat, list]) => (
                <optgroup key={cat} label={cat}>
                  {list.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold flex items-center gap-1.5">
                Ville {cityOk ? <Check className="w-3 h-3 text-forest" /> : null}
              </label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Bordeaux"
                className="w-full mt-1 border border-border rounded-xl px-3 py-2 text-sm focus:border-brand outline-none"
                data-testid="qf-city"
                autoComplete="address-level2"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">CP</label>
              <input
                value={postal}
                onChange={(e) => setPostal(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="33000"
                className="w-full mt-1 border border-border rounded-xl px-3 py-2 text-sm focus:border-brand outline-none"
                data-testid="qf-postal"
                inputMode="numeric"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Délai souhaité</label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {[
                ["urgent", "Urgent"],
                ["1-semaine", "1 sem."],
                ["1-mois", "1 mois"],
                ["flexible", "Flexible"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setUrgency(k)}
                  className={`h-9 rounded-xl text-xs font-semibold tap-scale border ${urgency === k ? "bg-brand text-white border-brand" : "bg-white text-slate2 border-border"}`}
                  data-testid={`qf-urgency-${k}`}
                >{l}</button>
              ))}
            </div>
          </div>
          <button
            onClick={() => setStep(2)}
            disabled={!canNextStep1}
            className="w-full h-11 rounded-full bg-brand text-white font-semibold tap-scale disabled:opacity-40 flex items-center justify-center gap-1.5"
            data-testid="qf-next-btn"
          >
            Continuer <ArrowRight className="w-4 h-4" />
          </button>
          {!canNextStep1 && (
            <p className="text-[11px] text-slate2 text-center">
              {!descOk && "→ Décrivez votre besoin (min. 10 caractères). "}
              {!cityOk && "→ Renseignez votre ville."}
            </p>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          {/* Toggle Créer un compte / Se connecter */}
          <div className="grid grid-cols-2 gap-1 bg-bone rounded-full p-1">
            <button
              type="button"
              onClick={() => setMode("register")}
              className={`h-8 rounded-full text-xs font-bold tap-scale transition-colors ${mode === "register" ? "bg-brand text-white" : "text-slate2"}`}
              data-testid="qf-mode-register"
            >Créer un compte</button>
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`h-8 rounded-full text-xs font-bold tap-scale transition-colors ${mode === "login" ? "bg-brand text-white" : "text-slate2"}`}
              data-testid="qf-mode-login"
            >J&apos;ai déjà un compte</button>
          </div>

          {mode === "register" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Votre nom</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Prénom Nom"
                className="w-full mt-1 border border-border rounded-xl px-3 py-2 text-sm focus:border-brand outline-none"
                data-testid="qf-name"
                autoComplete="name"
              />
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Email</label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.fr"
                className="w-full border border-border rounded-xl pl-9 pr-3 py-2 text-sm focus:border-brand outline-none"
                data-testid="qf-email"
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Mot de passe (min. 6 caractères)</label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-border rounded-xl pl-9 pr-3 py-2 text-sm focus:border-brand outline-none"
                data-testid="qf-password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
              />
            </div>
          </div>

          {mode === "register" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Téléphone (optionnel)</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="06 12 34 56 78"
                className="w-full mt-1 border border-border rounded-xl px-3 py-2 text-sm focus:border-brand outline-none"
                data-testid="qf-phone"
                autoComplete="tel"
              />
            </div>
          )}

          <div className="flex items-start gap-2 text-[11px] text-slate2">
            <ShieldCheck className="w-3.5 h-3.5 text-forest shrink-0 mt-0.5" />
            <p>Vos coordonnées ne sont partagées qu&apos;avec les artisans que vous choisirez. Aucune revente. RGPD conforme.</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep(1)}
              className="h-11 px-4 rounded-full border border-border text-slate2 text-sm font-semibold tap-scale"
              data-testid="qf-back-btn"
            >Retour</button>
            <button
              onClick={submit}
              disabled={!canSubmit || busy}
              className="flex-1 h-11 rounded-full bg-brand text-white font-semibold tap-scale disabled:opacity-40 flex items-center justify-center gap-1.5"
              data-testid="qf-submit-btn"
            >
              {busy ? "Envoi…" : (mode === "login" ? <><LogIn className="w-4 h-4" /> Me connecter & envoyer</> : "Créer & recevoir mes devis")}
            </button>
          </div>
          {!canSubmit && (
            <p className="text-[11px] text-slate2 text-center">
              {mode === "register" && !nameOk && "→ Renseignez votre nom. "}
              {!emailOk && "→ Email valide requis. "}
              {!pwOk && "→ Mot de passe (6 caractères minimum)."}
            </p>
          )}

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] uppercase tracking-widest text-slate2">ou</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button"
            onClick={() => {
              // Persiste la demande en cours pour la finaliser après OAuth Google
              try {
                localStorage.setItem("bp_pending_lead", JSON.stringify({
                  description: description.trim(),
                  trade: trade || null,
                  city: city.trim(),
                  postal_code: postal || null,
                  urgency,
                  saved_at: new Date().toISOString(),
                }));
              } catch { /* localStorage indisponible */ }
              const redirectUrl = window.location.origin + "/auth-callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-11 rounded-full bg-white border border-border text-ink text-sm font-semibold tap-scale flex items-center justify-center gap-3"
            data-testid="qf-google-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.4C29.7 34.5 27 35.5 24 35.5c-5.2 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.5 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.4C41.9 35.7 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            <span>Continuer avec Google</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  useSeo();
  const nav = useNavigate();
  const [openFaq, setOpenFaq] = useState(-1);
  const [trades, setTrades] = useState([]);

  useEffect(() => {
    api.get("/trades")
      .then((r) => setTrades(r.data))
      .catch(() => {});
  }, []);

  const tradesByCat = useMemo(() => {
    const map = {};
    for (const t of trades) {
      const c = t.cat || "Autres";
      if (!map[c]) map[c] = [];
      map[c].push(t);
    }
    return map;
  }, [trades]);

  return (
    <div className="relative min-h-screen bg-bone text-ink" data-testid="landing-page">
      <ToolsBackdrop opacity={0.06} />

      {/* Nav */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <a href="/landing" className="flex items-center gap-2" data-testid="landing-logo-link">
          <img src="/brand/icon-192.png" alt="Besoin d'un Pro logo" className="h-10 w-10 rounded-2xl shadow" width={40} height={40} />
          <div>
            <div className="font-heading font-bold text-ink text-sm">Besoin d&apos;un Pro&nbsp;?</div>
            <div className="text-[10px] uppercase tracking-widest text-slate2">Artisans locaux vérifiés</div>
          </div>
        </a>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline-flex h-9 px-4 rounded-full text-sm font-semibold text-ink border border-border items-center tap-scale" data-testid="landing-login-btn">Connexion</Link>
          <Link to="/charter" className="h-9 px-4 rounded-full bg-pro text-white text-sm font-semibold flex items-center tap-scale" data-testid="landing-pro-btn">
            <Hammer className="w-3.5 h-3.5 mr-1.5" /> Je suis artisan
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-16 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="inline-flex items-center gap-2 bg-brand/10 text-brand rounded-full px-3 py-1 text-xs font-semibold mb-5" data-testid="landing-badge">
            <Sparkles className="w-3.5 h-3.5" /> 100% gratuit pour les particuliers
          </div>
          <div className="text-plate">
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight">
              La bonne personne.<br />
              Au bon endroit.<br />
              <span className="text-brand">Au bon moment.</span>
            </h1>
            <p className="text-slate2 text-base mt-5 max-w-md leading-relaxed">
              Trouvez un <strong>artisan de confiance près de chez vous en 30 secondes</strong> :
              plombier, électricien, peintre, maçon, couvreur, menuisier… Devis gratuits en 24 à 48 h,
              artisans <strong>vérifiés SIRET et certifiés RGE</strong>.
            </p>
          </div>
          <ul className="mt-6 space-y-2.5">
            {[
              { icon: ShieldCheck, t: "Artisans vérifiés SIRET + RGE (ADEME)" },
              { icon: Clock, t: "Jusqu'à 3 devis reçus en 24 à 48 h" },
              { icon: Star, t: "4,8 / 5 — plus de 1 200 avis clients" },
              { icon: MapPin, t: "300+ villes couvertes en France" },
            ].map((r, i) => {
              const Ico = r.icon;
              return (
                <li key={i} className="flex items-center gap-2.5 text-sm text-ink">
                  <span className="h-7 w-7 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
                    <Ico className="w-4 h-4" />
                  </span>
                  {r.t}
                </li>
              );
            })}
          </ul>
          <div className="mt-6 flex items-center gap-3 text-xs text-slate2">
            <div className="flex -space-x-2">
              {["#F26B1C","#0F1E3D","#2D6A4F"].map((c, i) => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-bone" style={{ background: c }} />
              ))}
            </div>
            <span>Rejoint par <strong className="text-ink">1 200+</strong> particuliers ce mois-ci</span>
          </div>
        </div>

        <div className="lg:sticky lg:top-6">
          <QuickForm trades={trades} tradesByCat={tradesByCat} />
        </div>
      </section>

      {/* Métiers populaires — SEO longtail */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12 border-t border-border/70" aria-labelledby="metiers-title">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-1">Métiers</div>
            <h2 id="metiers-title" className="font-heading font-bold text-2xl sm:text-3xl">Trouvez l&apos;artisan qu&apos;il vous faut</h2>
          </div>
          <span className="text-xs text-slate2 hidden sm:block">150+ métiers référencés</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="landing-trades-grid">
          {TOP_TRADES.map((t) => {
            const Ico = t.icon;
            return (
              <a
                key={t.slug}
                href={`/landing?metier=${t.slug}`}
                className="group bg-white rounded-2xl border border-border p-4 tap-scale hover:border-brand transition-colors"
                data-testid={`landing-trade-${t.slug}`}
              >
                <span className="h-10 w-10 rounded-xl bg-pro/10 text-pro flex items-center justify-center mb-3 group-hover:bg-brand/10 group-hover:text-brand transition-colors">
                  <Ico className="w-5 h-5" />
                </span>
                <div className="font-heading font-bold text-ink text-sm">{t.name}</div>
                <div className="text-[11px] text-slate2 mt-0.5">{t.blurb}</div>
              </a>
            );
          })}
        </div>
      </section>

      {/* Villes — SEO local */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12 border-t border-border/70" aria-labelledby="villes-title">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-1">Zones couvertes</div>
        <h2 id="villes-title" className="font-heading font-bold text-2xl sm:text-3xl mb-4">Artisans locaux dans les grandes villes de France</h2>
        <p className="text-sm text-slate2 max-w-2xl mb-6">
          Notre réseau couvre déjà plus de 300 villes en France métropolitaine, avec une géolocalisation précise pour vous
          proposer <strong>uniquement des artisans dans un rayon de 10 à 20 km</strong> autour de chez vous.
        </p>
        <div className="flex flex-wrap gap-2" data-testid="landing-cities-list">
          {TOP_CITIES.map((c) => (
            <a
              key={c}
              href={`/landing?ville=${encodeURIComponent(c)}`}
              className="inline-flex items-center gap-1.5 bg-white border border-border rounded-full px-3 py-1.5 text-xs font-semibold text-ink tap-scale hover:border-brand hover:text-brand"
              data-testid={`landing-city-${c.toLowerCase()}`}
            >
              <MapPin className="w-3 h-3" /> Artisan {c}
            </a>
          ))}
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-14 border-t border-border/70" aria-labelledby="how-title">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-1 text-center">Comment ça marche</div>
        <h2 id="how-title" className="font-heading font-bold text-2xl sm:text-3xl text-center mb-10">3 étapes, 3 minutes chrono</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { n: "01", t: "Décrivez votre besoin", d: "Type de travaux, ville, délai, coordonnées. Aucun compte requis pour commencer.", icon: Sparkles },
            { n: "02", t: "Recevez 3 devis vérifiés", d: "Nos artisans locaux, vérifiés SIRET et certifiés RGE, vous répondent en 24 à 48 h.", icon: ShieldCheck },
            { n: "03", t: "Choisissez le meilleur", d: "Comparez prix, délais, avis, certifications. Discutez directement en messagerie. Sélectionnez l'artisan idéal.", icon: Users },
          ].map((s, i) => {
            const Ico = s.icon;
            return (
              <div key={i} className="bg-white rounded-3xl border border-border p-6" data-testid={`landing-step-${i+1}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-10 w-10 rounded-xl bg-pro text-white flex items-center justify-center font-heading font-bold">{s.n}</span>
                  <Ico className="w-5 h-5 text-brand" />
                </div>
                <h3 className="font-heading font-bold text-ink">{s.t}</h3>
                <p className="text-sm text-slate2 mt-1.5 leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Preuves sociales — reviews */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-14 border-t border-border/70" aria-labelledby="reviews-title">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-1">Ils nous font confiance</div>
        <div className="flex items-center gap-3 mb-6">
          <h2 id="reviews-title" className="font-heading font-bold text-2xl sm:text-3xl">4,8 / 5 sur plus de 1 200 avis</h2>
          <div className="flex items-center">
            {[1,2,3,4,5].map((n) => <Star key={n} className="w-4 h-4 fill-brand text-brand" />)}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4" data-testid="landing-reviews">
          {REVIEWS.map((r, i) => (
            <article key={i} className="bg-white rounded-2xl border border-border p-5" data-testid={`landing-review-${i}`}>
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: r.stars }).map((_, k) => <Star key={k} className="w-4 h-4 fill-brand text-brand" />)}
              </div>
              <p className="text-sm text-ink leading-relaxed">« {r.text} »</p>
              <div className="mt-3 text-[11px] uppercase tracking-widest text-slate2 font-semibold">
                {r.name} · {r.city} · {r.trade}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Bloc Pro — recrutement */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-14 border-t border-border/70">
        <div className="bg-pro text-white rounded-3xl p-8 sm:p-10 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2">Vous êtes artisan ?</div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl">Développez votre chiffre d&apos;affaires avec des leads qualifiés</h2>
            <p className="text-white/80 text-sm mt-2 leading-relaxed">
              Recevez des demandes clients ciblées par métier et par zone. Payez uniquement quand vous débloquez un contact
              qui vous intéresse (3€ à 10€/lead). Abonnement Pro à 9,99€/mois pour l&apos;accès illimité.
            </p>
            <div className="flex items-center gap-4 mt-4 text-xs text-white/70">
              <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> +32% CA moyen</span>
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> 800 pros actifs</span>
              <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Sans engagement</span>
            </div>
          </div>
          <div className="flex justify-end">
            <Link to="/charter" className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-5 py-3 text-sm font-semibold tap-scale" data-testid="landing-pro-cta">
              Rejoindre les Pros <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 py-14 border-t border-border/70" aria-labelledby="faq-title">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-1 text-center">Questions fréquentes</div>
        <h2 id="faq-title" className="font-heading font-bold text-2xl sm:text-3xl text-center mb-6">On vous répond en toute transparence</h2>
        <div className="space-y-2" data-testid="landing-faq">
          {FAQ.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl border border-border overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between px-4 py-3.5 text-left tap-scale"
                data-testid={`landing-faq-${i}`}
                aria-expanded={openFaq === i}
              >
                <span className="font-heading font-bold text-sm text-ink">{f.q}</span>
                <span className={`transition-transform ${openFaq === i ? "rotate-45" : ""} text-slate2 text-lg leading-none`}>+</span>
              </button>
              {openFaq === i && (
                <div className="px-4 pb-4 text-sm text-slate2 leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-16 pt-4 text-center">
        <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-3">Prêt à trouver votre artisan&nbsp;?</h2>
        <p className="text-sm text-slate2 mb-5">Décrivez votre besoin, on s&apos;occupe du reste. C&apos;est gratuit, ça prend 30 secondes.</p>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-6 py-3 text-sm font-semibold tap-scale"
          data-testid="landing-scroll-top-cta"
        >
          Recevoir mes devis gratuits <ArrowRight className="w-4 h-4" />
        </button>
      </section>

      {/* Footer — masqué dans l'application native (TWA / PWA installée) */}
      {!isNativeApp() && (
      <footer className="relative z-10 bg-pro text-white/90 mt-6">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-4 gap-6">
          <div>
            <div className="flex items-center gap-2">
              <img src="/brand/icon-192.png" alt="Besoin d'un Pro logo" className="h-9 w-9 rounded-xl" width={36} height={36} />
              <div className="font-heading font-bold">Besoin d&apos;un Pro&nbsp;?</div>
            </div>
            <p className="text-xs text-white/60 mt-3 leading-relaxed">
              La marketplace française qui connecte particuliers et artisans du bâtiment vérifiés.
            </p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-2">Particuliers</div>
            <ul className="space-y-1.5 text-sm text-white/80">
              <li><a href="/landing" className="hover:text-brand">Créer une demande</a></li>
              <li><a href="/login" className="hover:text-brand">Se connecter</a></li>
              <li><a href="/landing#faq-title" className="hover:text-brand">FAQ</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-2">Artisans</div>
            <ul className="space-y-1.5 text-sm text-white/80">
              <li><Link to="/charter" className="hover:text-brand">Devenir Pro</Link></li>
              <li><a href="/landing#pro" className="hover:text-brand">Tarifs</a></li>
              <li><Link to="/login" className="hover:text-brand">Espace Pro</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-2">Légal</div>
            <ul className="space-y-1.5 text-sm text-white/80">
              <li><Link to="/legal/privacy" className="hover:text-brand">Politique de confidentialité</Link></li>
              <li><Link to="/legal/data-deletion" className="hover:text-brand">Suppression du compte</Link></li>
              <li><a href="/sitemap.xml" className="hover:text-brand">Plan du site</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/50">
          © {new Date().getFullYear()} Besoin d&apos;un Pro ? — La bonne personne, au bon endroit, au bon moment.<br />
          <a href="mailto:besoindunpro@gmail.com" className="hover:text-brand">besoindunpro@gmail.com</a>
        </div>
      </footer>
      )}
    </div>
  );
}
